import { z } from 'zod';
const STRICT_HOURS = [
    { maxAge: 5, hours: 6 },
    { maxAge: 8, hours: 8 },
    { maxAge: 15, hours: 9 },
    { maxAge: 17, hours: 10 },
];
/** The catch-all rule (unspecified state) — always defined so resolution never returns undefined. */
const DEFAULT_ENTERTAINMENT_RULE = {
    label: 'Other / unspecified',
    permitRequired: true,
    permitValidityMonths: 12,
    cooganRequired: false,
    studioTeacherRequired: true,
    maxHoursByAge: STRICT_HOURS,
};
/** Keyed by US state (or `DEFAULT`). Coogan-account states: CA, NY, LA, NM (and NC in practice). */
export const ENTERTAINMENT_JURISDICTIONS = {
    CA: { label: 'California', permitRequired: true, permitValidityMonths: 6, cooganRequired: true, studioTeacherRequired: true, maxHoursByAge: STRICT_HOURS },
    NY: { label: 'New York', permitRequired: true, permitValidityMonths: 12, cooganRequired: true, studioTeacherRequired: true, maxHoursByAge: STRICT_HOURS },
    LA: { label: 'Louisiana', permitRequired: true, permitValidityMonths: 12, cooganRequired: true, studioTeacherRequired: true, maxHoursByAge: STRICT_HOURS },
    NM: { label: 'New Mexico', permitRequired: true, permitValidityMonths: 12, cooganRequired: true, studioTeacherRequired: true, maxHoursByAge: STRICT_HOURS },
    GA: { label: 'Georgia', permitRequired: false, permitValidityMonths: 12, cooganRequired: false, studioTeacherRequired: true, maxHoursByAge: STRICT_HOURS },
    IL: { label: 'Illinois', permitRequired: true, permitValidityMonths: 12, cooganRequired: false, studioTeacherRequired: true, maxHoursByAge: STRICT_HOURS },
    TX: { label: 'Texas', permitRequired: false, permitValidityMonths: 12, cooganRequired: false, studioTeacherRequired: false, maxHoursByAge: STRICT_HOURS },
    FL: { label: 'Florida', permitRequired: false, permitValidityMonths: 12, cooganRequired: false, studioTeacherRequired: true, maxHoursByAge: STRICT_HOURS },
    DEFAULT: DEFAULT_ENTERTAINMENT_RULE,
};
export function getEntertainmentRule(state) {
    const code = (state ?? '').trim().toUpperCase();
    return ENTERTAINMENT_JURISDICTIONS[code] ?? DEFAULT_ENTERTAINMENT_RULE;
}
/** Whether a work permit is required for a performer in `state` at `age` (adults never need one). */
export function permitRequired(state, age) {
    if (age != null && age >= 18)
        return false;
    return getEntertainmentRule(state).permitRequired;
}
export function cooganRequired(state) {
    return getEntertainmentRule(state).cooganRequired;
}
/** Max on-set work hours for a performer of `age` in `state`. */
export function workHoursLimit(state, age) {
    const rule = getEntertainmentRule(state);
    const band = rule.maxHoursByAge.find((b) => age <= b.maxAge);
    return band?.hours ?? rule.maxHoursByAge[rule.maxHoursByAge.length - 1]?.hours ?? 10;
}
/** True if a permit expiry is within `withinDays` (or already past). */
export function permitExpirySoon(expiry, at = new Date(), withinDays = PERMIT_LEAD_DAYS) {
    if (!expiry)
        return false;
    const exp = expiry instanceof Date ? expiry : new Date(expiry);
    if (Number.isNaN(exp.getTime()))
        return false;
    return exp.getTime() - at.getTime() <= withinDays * 86_400_000;
}
// ---------------------------------------------------------------------------
// Work-permit dates by UTC day: THE window of the reminders and the Phase 5 warnings (one profile, spec 3.18)
// ---------------------------------------------------------------------------
/** A permit expiring within this many days is coming up: the reminders' lead time, and a warning. */
export const PERMIT_LEAD_DAYS = 30;
/** The last week before the expiry, the expiry day included: the final stage. */
export const PERMIT_FINAL_DAYS = 7;
/** The reminders stop this many days after the expiry, so a long-lapsed permit stops nagging. */
export const PERMIT_LAPSED_DAYS = 60;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
/** PURE: whole UTC days from `today` to a permit's expiry (yyyy-mm-dd): 0 on the expiry day, negative after it; null without a readable date. */
export function permitDaysLeft(expiry, today) {
    const day = typeof expiry === 'string' ? expiry.slice(0, 10) : '';
    if (!ISO_DAY.test(day) || !ISO_DAY.test(today))
        return null;
    const at = Date.parse(`${day}T00:00:00Z`);
    const from = Date.parse(`${today}T00:00:00Z`);
    if (Number.isNaN(at) || Number.isNaN(from))
        return null;
    return Math.round((at - from) / 86_400_000);
}
/**
 * PURE: the stage of a dated permit by its days left, or null outside every stage (further off than the lead time, or
 * lapsed past the reminders). The three stages cover exactly the reminders' window, from PERMIT_LAPSED_DAYS past
 * through PERMIT_LEAD_DAYS ahead, so the reminder set and the warning notices can never disagree about a date.
 */
export function permitStageOf(daysLeft) {
    if (daysLeft > PERMIT_LEAD_DAYS || daysLeft < -PERMIT_LAPSED_DAYS)
        return null;
    if (daysLeft < 0)
        return 'expired';
    return daysLeft <= PERMIT_FINAL_DAYS ? 'final' : 'expiring';
}
/**
 * PURE: a permit is KNOWN to be required (the Phase 5 "missing" warning): the state is a known jurisdiction that
 * requires one (never the catch-all rule's guess for an unknown state) and the age is known and under 18.
 */
export function permitKnownRequired(state, age) {
    const code = (state ?? '').trim().toUpperCase();
    const rule = code && code !== 'DEFAULT' && Object.hasOwn(ENTERTAINMENT_JURISDICTIONS, code) ? ENTERTAINMENT_JURISDICTIONS[code] : undefined;
    return !!rule?.permitRequired && age != null && age < 18;
}
/**
 * PURE: the warning a row shows (Phase 5; a warning never blocks anything), from the days left of its expiry: expired
 * after the expiry day (however long ago: the reminders stop, the warning stays), expiring through the lead time (the
 * expiry day is the last valid day); with no date, missing only where a permit is known to be required and none is
 * pending. Null otherwise.
 */
export function permitWarningKindOf(daysLeft, missing) {
    if (daysLeft === null)
        return missing.required && !missing.pending ? 'missing' : null;
    if (daysLeft < 0)
        return 'expired';
    return daysLeft <= PERMIT_LEAD_DAYS ? 'expiring' : null;
}
// ---------------------------------------------------------------------------
// Schema (matches the child_performer_profiles table; 0069 migration)
// ---------------------------------------------------------------------------
export const WORK_PERMIT_STATUSES = ['none', 'pending', 'active', 'expired'];
export const workPermitStatus = z.enum(WORK_PERMIT_STATUSES);
/** Sensitive fields encrypted at rest (enc:v1 AES-256-GCM) — never returned or logged in the clear. */
export const SENSITIVE_PERFORMER_FIELDS = ['work_permit_number', 'coogan_account_last4'];
export const childPerformerProfileSchema = z.object({
    managed_person_id: z.string(),
    entertainment_state: z.string().nullable().optional(),
    work_permit_number: z.string().nullable().optional(),
    work_permit_status: workPermitStatus,
    work_permit_expiry: z.union([z.string(), z.date()]).nullable().optional(),
    coogan_account_bank: z.string().nullable().optional(),
    coogan_account_last4: z.string().nullable().optional(),
    coogan_verified: z.boolean(),
    on_set_guardian_name: z.string().nullable().optional(),
    hours_restriction: z.record(z.string(), z.unknown()).nullable().optional(),
});
export const childPerformerPatchSchema = z
    .object({
    entertainment_state: z.string().trim().max(4).nullable(),
    work_permit_number: z.string().trim().max(120).nullable(),
    work_permit_status: workPermitStatus,
    work_permit_expiry: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    coogan_account_bank: z.string().trim().max(120).nullable(),
    coogan_account_last4: z.string().trim().max(8).nullable(),
    coogan_verified: z.boolean(),
    on_set_guardian_name: z.string().trim().max(160).nullable(),
})
    .partial();
/*
 * The performer panel's own line below is kept exactly as it was: it reads the person's permit STATUS (pending or
 * inactive reads as missing, under the catch-all rule too) and counts the expiry day itself as expired. The one-profile
 * warnings (permitWarningKindOf above, per agency row and by UTC day) are a different, date-only rule on purpose: a date
 * on file always counts, the expiry day is the last valid day, and "missing" needs a known state, a known age and no
 * pending permit. Where the server sends warnings the booking shows them in place of this line.
 */
/** Human-readable compliance gaps for a performer — powers RepTree warning badges + booking gates. */
export function performerComplianceIssues(profile, age, at = new Date()) {
    const issues = [];
    const state = profile?.entertainment_state;
    if (permitRequired(state, age)) {
        if (!profile || profile.work_permit_status !== 'active')
            issues.push('Work permit missing or inactive');
        else if (permitExpirySoon(profile.work_permit_expiry, at, 0))
            issues.push('Work permit expired');
        else if (permitExpirySoon(profile.work_permit_expiry, at))
            issues.push('Work permit expiring soon');
    }
    if (cooganRequired(state) && !profile?.coogan_verified) {
        issues.push('Coogan / trust account not verified');
    }
    return issues;
}
//# sourceMappingURL=child-performer.js.map